import React, { useCallback, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Button, Input, Popover, Stack, Typography, useSnackbar } from '@lyearn/ui';
import { NoteReviewStatusTypeEnum } from '@/types/schema';

import { useReviewNoteMutation } from '../../graphql/mutations/generated/reviewNote';
import { NoteFragment } from '../../graphql/queries/generated/notes';

function ManageApprovalButtons({
  note,
  setCurrentNote,
}: {
  note: NoteFragment;
  setCurrentNote: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
}) {
  const { t } = useTranslation(['Common', 'Coach']);
  const [reason, setReason] = useState('');
  const [, reviewNote] = useReviewNoteMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();

  const handleApprove = useCallback(async () => {
    if (!note.id) {
      return;
    }

    try {
      const response = await reviewNote({
        input: {
          noteId: note.id,
          reviewStatus: NoteReviewStatusTypeEnum.Approved,
        },
      });

      if (response?.data?.reviewNote?.success) {
        showSnackbar({ message: 'Note approved!' });
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
      setCurrentNote(null);
    } catch (error) {
      showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
    }
  }, [reviewNote, note.id, showSnackbar, showErrorSnackbar, t]);

  const handleRevise = useCallback(
    async (reason: string) => {
      if (!note.id) {
        return;
      }

      try {
        const response = await reviewNote({
          input: {
            noteId: note.id,
            reviewStatus: NoteReviewStatusTypeEnum.Rejected,
            reviewStatusReason: reason,
          },
        });

        if (response?.data?.reviewNote?.success) {
          showSnackbar({ message: 'Note sent for revision!' });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        }
        setCurrentNote(null);
      } catch (error) {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
    },
    [reviewNote, note.id, showSnackbar, showErrorSnackbar, t],
  );

  return (
    <>
      <Button size="small" onClick={handleApprove}>
        {t('Coach:NotesPanel.Approve')}
      </Button>
      <Popover>
        <Popover.Button>
          <Button size="small" variant="outlined">
            {t('Coach:NotesPanel.Revise')}
          </Button>
        </Popover.Button>
        <Popover.Content>
          <Stack className="p-24 max-w-[48rem]" direction="col" gap={20}>
            <Stack className="ms-8" direction="col" gap={4}>
              <Typography variant="heading-m">
                {t('Coach:NotesPanel.SendForRevisionTitle')}
              </Typography>
              <Typography color="text-text-secondary" variant="body-short-01">
                {t('Coach:NotesPanel.SendForRevisionDescription')}
              </Typography>
            </Stack>

            <Input
              multiline
              placeholder={t('Coach:NotesPanel.TypeHerePlaceholder')}
              rows={4}
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                setReason(e.target.value)
              }
            />

            <Stack
              alignItems="center"
              className="mt-24"
              direction="row"
              gap={8}
              justifyContent="end">
              {/* <Button appearance="secondary" variant="outlined" onClick={() => {}}>
                Cancel
              </Button> */}
              <Button onClick={() => handleRevise(reason)}>{t('Coach:NotesPanel.Send')}</Button>
            </Stack>
          </Stack>
        </Popover.Content>
      </Popover>
    </>
  );
}

export default ManageApprovalButtons;
